import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResourcePicker } from "@/components/ResourcePicker";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BookOpen, ExternalLink, GripVertical, Layers, Pencil, Plus, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/hooks/useConfirm";

const LEVELS = ["A1", "A2", "B1", "B2", "C1"];
const VACIA = { title: "", class_count: 1, objectives: "", grammar: "", vocab: "", materials: [], practices: [] };

const lineasAArray = (txt) => (txt || "").split("\n").map((l) => l.trim()).filter(Boolean);
const arrayALineas = (arr) => (arr || []).join("\n");

/** Plan de estudio: se escribe una vez y los profesores lo leen. */
export default function StudyPlans() {
  const confirm = useConfirm();
  const [tab, setTab] = useState("planes");
  const [plans, setPlans] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [planId, setPlanId] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [board, setBoard] = useState([]);
  const [openUnit, setOpenUnit] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIA);
  const [picker, setPicker] = useState("");   // "materials" | "practices" | ""
  const [catalogo, setCatalogo] = useState([]);
  const [nuevo, setNuevo] = useState({ scope: "level", key: "A1" });

  const cargarPlanes = () => api.get("/study-plans").then((r) => setPlans(r.data || []));
  useEffect(() => {
    cargarPlanes().catch(() => setPlans([]));
    api.get("/programs").then((r) => setPrograms(r.data || [])).catch(() => setPrograms([]));
    api.get("/resources").then((r) => setCatalogo(r.data || [])).catch(() => setCatalogo([]));
  }, []);

  useEffect(() => {
    if (!planId) { setDetalle(null); return; }
    api.get(`/study-plans/${planId}`).then((r) => setDetalle(r.data)).catch(() => setDetalle(null));
  }, [planId]);

  useEffect(() => {
    if (tab !== "avance") return;
    api.get("/study-plans/board/progress").then((r) => setBoard(r.data || [])).catch(() => setBoard([]));
  }, [tab]);

  const recargarDetalle = () => api.get(`/study-plans/${planId}`).then((r) => setDetalle(r.data));

  const crearPlan = async () => {
    try {
      const r = await api.post("/study-plans", nuevo);
      toast.success("Plan creado");
      await cargarPlanes();
      setPlanId(r.data.id);
      // El nivel recién creado ya no está libre: se deja el siguiente a mano.
      if (nuevo.scope === "level") {
        setNuevo({ scope: "level", key: LEVELS.find((l) => l !== nuevo.key && !plans.some((p) => p.scope === "level" && p.key === l)) || "" });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear"); }
  };

  const borrarPlan = async () => {
    if (!await confirm({ title: "¿Borrar este plan?", description: "Se borran sus unidades y el avance marcado por los profes." })) return;
    await api.delete(`/study-plans/${planId}`);
    setPlanId("");
    cargarPlanes();
    toast.success("Plan borrado");
  };

  const guardarDoc = async (url) => {
    await api.patch(`/study-plans/${planId}`, { source_doc_url: url || null });
    recargarDetalle();
    toast.success("Documento guardado");
  };

  const abrirNueva = () => { setEditando(null); setForm(VACIA); setOpenUnit(true); };
  const abrirEditar = (u) => {
    setEditando(u);
    setForm({
      title: u.title, class_count: u.class_count ?? 1,
      objectives: arrayALineas(u.objectives), grammar: arrayALineas(u.grammar),
      vocab: u.vocab || "", materials: u.materials || [], practices: u.practices || [],
    });
    setOpenUnit(true);
  };

  const guardarUnidad = async () => {
    if (!form.title.trim()) { toast.error("Ponele un título a la unidad"); return; }
    const payload = {
      title: form.title.trim(),
      class_count: Number(form.class_count) || 1,
      objectives: lineasAArray(form.objectives),
      grammar: lineasAArray(form.grammar),
      vocab: form.vocab.trim() || null,
      materials: form.materials,
      practices: form.practices,
    };
    try {
      if (editando) await api.patch(`/plan-units/${editando.id}`, payload);
      else await api.post(`/study-plans/${planId}/units`, payload);
      setOpenUnit(false);
      recargarDetalle();
      cargarPlanes();
      toast.success("Unidad guardada");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const borrarUnidad = async (u) => {
    if (!await confirm({ title: `¿Borrar "${u.title}"?` })) return;
    await api.delete(`/plan-units/${u.id}`);
    recargarDetalle();
    cargarPlanes();
  };

  const agregarRecursos = (ids) => {
    const campo = picker;
    setPicker("");
    const nuevos = ids.map((id) => ({
      kind: campo === "practices" ? "practice" : "resource",
      name: catalogo.find((c) => c.id === id)?.title || "Material",
      resource_id: id, url: null,
    }));
    setForm((f) => ({ ...f, [campo]: [...f[campo], ...nuevos] }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = detalle.units.map((u) => u.id);
    const nuevos = arrayMove(detalle.units, ids.indexOf(active.id), ids.indexOf(over.id));
    const previos = detalle.units;
    setDetalle((d) => ({ ...d, units: nuevos }));
    try {
      await api.patch(`/study-plans/${planId}/units/reorder`, { ids: nuevos.map((u) => u.id) });
    } catch (e) {
      setDetalle((d) => ({ ...d, units: previos }));
      toast.error(e?.response?.data?.detail || "No se pudo guardar el orden");
    }
  };

  const porNivel = useMemo(() => plans.filter((p) => p.scope === "level"), [plans]);
  const porPrograma = useMemo(() => plans.filter((p) => p.scope === "program"), [plans]);
  const nivelesLibres = LEVELS.filter((l) => !porNivel.some((p) => p.key === l));

  // El nivel propuesto siempre tiene que ser uno que todavía no tenga plan.
  useEffect(() => {
    if (nuevo.scope !== "level") return;
    if (!nivelesLibres.includes(nuevo.key)) setNuevo({ scope: "level", key: nivelesLibres[0] || "" });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [plans]);

  return (
    <div className="space-y-5" data-testid="study-plans-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-brand-blue" /> Plan de estudio
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          El temario de cada nivel. Los profesores lo leen y marcan por dónde van; no lo pueden cambiar.
        </p>
      </div>

      <div className="flex gap-1.5">
        {[["planes", "Planes y unidades"], ["avance", "Avance de las clases"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`study-plans-tab-${k}`}
                  className={`text-[13px] font-semibold px-3.5 py-2 rounded-full transition-colors ${
                    tab === k ? "bg-[#0d1b2a] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "avance" ? (
        <Card className="overflow-hidden" data-testid="study-plans-board">
          {board.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">
              Todavía ninguna clase tiene plan asignado. Marcales el nivel en «Programas y clases».
            </p>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-slate-50 text-[10.5px] uppercase tracking-wider text-slate-400">
                  <th className="text-left font-bold px-4 py-2.5">Clase</th>
                  <th className="text-left font-bold px-4 py-2.5 hidden sm:table-cell">Profe</th>
                  <th className="text-left font-bold px-4 py-2.5">Plan</th>
                  <th className="text-left font-bold px-4 py-2.5 w-[190px]">Avance</th>
                  <th className="text-left font-bold px-4 py-2.5 hidden md:table-cell">Unidad de hoy</th>
                </tr>
              </thead>
              <tbody>
                {board.map((f) => {
                  const pct = f.units_total ? Math.round((f.units_done / f.units_total) * 100) : 0;
                  return (
                    <tr key={f.group_id} className="border-t border-slate-100 hover:bg-slate-50/70"
                        data-testid={`board-row-${f.group_id}`}>
                      <td className="px-4 py-3">
                        <b className="text-slate-900">{f.group_name}</b>
                        <div className="text-[11px] text-slate-500">{f.schedule || "—"}</div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">{f.teacher_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="text-[11px] font-bold rounded px-1.5 py-[2px] bg-brand-blue/10 text-brand-blue">
                          {f.level || f.plan_label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-[6px] rounded-full bg-slate-200 overflow-hidden">
                          <div className="h-full rounded-full bg-brand-blue" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[11px] text-slate-500 mt-1">
                          {f.units_done} de {f.units_total} unidades · {f.classes_done} de {f.classes_total} clases
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {f.current_unit || <em className="text-slate-400 not-italic">Terminaron el plan</em>}
                        {f.last_marked_on && <div className="text-[11px] text-slate-400">último: {f.last_marked_on}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-5">
          <div className="space-y-1.5">
            {porNivel.map((p) => (
              <PlanRow key={p.id} plan={p} activo={p.id === planId} onClick={() => setPlanId(p.id)} />
            ))}
            {porPrograma.length > 0 && (
              <p className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold pt-3 px-1">Por programa</p>
            )}
            {porPrograma.map((p) => (
              <PlanRow key={p.id} plan={p} activo={p.id === planId} onClick={() => setPlanId(p.id)} />
            ))}

            <Card className="p-3 mt-4" data-testid="new-plan-card">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2">Crear un plan</p>
              <Select value={nuevo.scope} onValueChange={(v) =>
                setNuevo({ scope: v, key: v === "level" ? (nivelesLibres[0] || "A1") : (programs[0]?.id || "") })}>
                <SelectTrigger className="h-9 mb-2" data-testid="new-plan-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="level">Para un nivel</SelectItem>
                  <SelectItem value="program">Para un programa</SelectItem>
                </SelectContent>
              </Select>
              <Select value={nuevo.key} onValueChange={(v) => setNuevo({ ...nuevo, key: v })}>
                <SelectTrigger className="h-9 mb-2" data-testid="new-plan-key"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {nuevo.scope === "level"
                    ? nivelesLibres.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)
                    : programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="w-full btn-brand text-white" onClick={crearPlan} data-testid="new-plan-save">
                <Plus className="h-3.5 w-3.5" /> Crear
              </Button>
            </Card>
          </div>

          {!detalle ? (
            <Card className="p-10 text-center text-slate-500" data-testid="study-plans-pick-hint">
              Elegí un nivel para armar su temario
            </Card>
          ) : (
            <div>
              <Card className="p-4 mb-3.5">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold text-[17px] tracking-tight">{detalle.label}</div>
                    <div className="text-[11.5px] text-slate-500">
                      {detalle.units.length} unidades ·{" "}
                      {detalle.units.reduce((a, u) => a + (Number(u.class_count) || 0), 0)} clases
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={borrarPlan} data-testid="delete-plan">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" className="btn-brand text-white" onClick={abrirNueva} data-testid="new-unit">
                    <Plus className="h-3.5 w-3.5" /> Nueva unidad
                  </Button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Input key={detalle.id} defaultValue={detalle.source_doc_url || ""} placeholder="Enlace del documento de Drive con el plan"
                         className="h-9 text-[13px]" data-testid="plan-doc-input"
                         onBlur={(e) => e.target.value !== (detalle.source_doc_url || "") && guardarDoc(e.target.value.trim())} />
                  {detalle.source_doc_url && (
                    <a href={detalle.source_doc_url} target="_blank" rel="noreferrer" className="shrink-0"
                       data-testid="plan-doc-open">
                      <Button size="sm" variant="outline"><ExternalLink className="h-3.5 w-3.5" /></Button>
                    </a>
                  )}
                </div>
              </Card>

              {detalle.units.length > 1 && (
                <p className="text-[11px] text-slate-400 mb-2 flex items-center gap-1" data-testid="units-reorder-hint">
                  <GripVertical className="h-3 w-3" /> Arrastrá las unidades para cambiar el orden
                </p>
              )}

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={detalle.units.map((u) => u.id)} strategy={verticalListSortingStrategy}>
                  {detalle.units.map((u, i) => (
                    <UnitRow key={u.id} unit={u} index={i}
                             onEdit={() => abrirEditar(u)} onDelete={() => borrarUnidad(u)} />
                  ))}
                </SortableContext>
              </DndContext>

              {detalle.units.length === 0 && (
                <Card className="p-10 text-center text-sm text-slate-500" data-testid="no-units">
                  Este plan todavía no tiene unidades
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      <Dialog open={openUnit} onOpenChange={setOpenUnit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="unit-dialog">
          <DialogHeader><DialogTitle>{editando ? "Editar unidad" : "Nueva unidad"}</DialogTitle></DialogHeader>
          <div className="space-y-3.5">
            <div className="grid grid-cols-[1fr_110px] gap-3">
              <div>
                <Label>Título</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       placeholder="Describir a la gente que uno quiere" data-testid="unit-title" />
              </div>
              <div>
                <Label>Clases</Label>
                <Input type="number" min="1" value={form.class_count}
                       onChange={(e) => setForm({ ...form, class_count: e.target.value })} data-testid="unit-classes" />
              </div>
            </div>
            <div>
              <Label>Al terminar, el estudiante puede… <span className="text-slate-400 font-normal">(uno por línea)</span></Label>
              <Textarea rows={3} value={form.objectives} data-testid="unit-objectives"
                        onChange={(e) => setForm({ ...form, objectives: e.target.value })}
                        placeholder={"Describir a un familiar\nComparar dos personas sin trabarse"} />
            </div>
            <div>
              <Label>Gramática <span className="text-slate-400 font-normal">(una por línea)</span></Label>
              <Textarea rows={2} value={form.grammar} data-testid="unit-grammar"
                        onChange={(e) => setForm({ ...form, grammar: e.target.value })} />
            </div>
            <div>
              <Label>Vocabulario</Label>
              <Input value={form.vocab} onChange={(e) => setForm({ ...form, vocab: e.target.value })}
                     placeholder="52 palabras: familia, carácter, rasgos físicos" data-testid="unit-vocab" />
            </div>
            <Adjuntos titulo="Materiales de la unidad" campo="materials" form={form} setForm={setForm}
                      onPick={() => setPicker("materials")} />
            <Adjuntos titulo="Prácticas y tareas" campo="practices" form={form} setForm={setForm}
                      onPick={() => setPicker("practices")} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUnit(false)}>Cancelar</Button>
            <Button className="btn-brand text-white" onClick={guardarUnidad} data-testid="unit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResourcePicker open={!!picker} onOpenChange={(v) => !v && setPicker("")} selected={[]}
                      excludeIds={picker ? (form[picker] || []).map((m) => m.resource_id).filter(Boolean) : []}
                      onConfirm={agregarRecursos} />
    </div>
  );
}

const PlanRow = ({ plan, activo, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`plan-row-${plan.id}`}
          className={`w-full flex items-center gap-2.5 border rounded-xl px-3 py-2.5 bg-white text-left
            transition-all hover:translate-x-[3px] ${activo ? "border-brand-blue bg-brand-blue/[0.04]" : "border-slate-200"}`}>
    <Layers className="h-3.5 w-3.5 text-slate-400 shrink-0" />
    <b className="text-[13.5px] flex-1 min-w-0 truncate">{plan.scope === "level" ? plan.key : plan.label}</b>
    <span className="text-[11px] text-slate-500 shrink-0">{plan.units_count} un.</span>
  </button>
);

const UnitRow = ({ unit, index, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: unit.id });
  return (
    <div ref={setNodeRef}
         style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 20 : undefined,
                  position: isDragging ? "relative" : undefined }}
         className={`flex items-center gap-3 border rounded-xl px-3.5 py-3 bg-white mb-2 transition-shadow
           ${isDragging ? "shadow-lg border-slate-300" : "border-slate-200 hover:border-slate-300"}`}
         data-testid={`unit-row-${unit.id}`}>
      <button type="button" {...attributes} {...listeners} title="Arrastrar para acomodar"
              className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
              data-testid={`unit-drag-${unit.id}`}>
        <GripVertical className="h-4 w-4" />
      </button>
      <b className="text-[12px] text-slate-400 w-4 shrink-0">{index + 1}</b>
      <span className="flex-1 min-w-0 text-[13.5px] font-semibold truncate">{unit.title}</span>
      <span className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-[2px] shrink-0 hidden sm:inline">
        {unit.class_count} {unit.class_count === 1 ? "clase" : "clases"}
      </span>
      <span className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-[2px] shrink-0 hidden sm:inline">
        {(() => {
          const n = (unit.materials?.length || 0) + (unit.practices?.length || 0);
          return `${n} ${n === 1 ? "archivo" : "archivos"}`;
        })()}
      </span>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={onEdit} data-testid={`unit-edit-${unit.id}`}>
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={onDelete} data-testid={`unit-delete-${unit.id}`}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
};

const Adjuntos = ({ titulo, campo, form, setForm, onPick }) => (
  <div>
    <div className="flex items-center gap-2 mb-1.5">
      <Label className="flex-1">{titulo}</Label>
      <Button size="sm" variant="outline" onClick={onPick} data-testid={`unit-pick-${campo}`}>
        <Plus className="h-3.5 w-3.5" /> De la biblioteca
      </Button>
    </div>
    {form[campo].length === 0 ? (
      <p className="text-[12px] text-slate-400">Sin archivos</p>
    ) : (
      <div className="flex flex-col gap-1.5">
        {form[campo].map((m, k) => (
          <div key={k} className="flex items-center gap-2 text-[13px] border border-slate-200 rounded-lg px-3 py-1.5"
               data-testid={`unit-${campo}-${k}`}>
            <span className="flex-1 min-w-0 truncate">{m.name}</span>
            <button type="button" className="text-slate-400 hover:text-red-600"
                    onClick={() => setForm((f) => ({ ...f, [campo]: f[campo].filter((_, i) => i !== k) }))}
                    data-testid={`unit-${campo}-remove-${k}`}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);
